import { gsap } from "gsap";
import { StateHandler } from "../../utils/StateHandler";

export class PresentationState extends StateHandler {

    public static readonly INACTIVE: number = 0;  // INACTIVE
    public static readonly ACTIVATED: number = 1;  // ACTIVATED

    constructor(state: number = PresentationState.INACTIVE) { super(state); }

    public get isActive(): boolean {
        return this.equal(PresentationState.ACTIVATED);
    }

    public setInactive(): void { this.set(PresentationState.INACTIVE); }
}

export abstract class Presentation {

    /* The Core Timeline */
    private _timeline: gsap.core.Timeline;
    /* ---------------------------------------- */

    /* The Core State */
    private _presentationState: PresentationState = new PresentationState(PresentationState.INACTIVE);
    /* ---------------------------------------- */


    /* The Optional configs */
    public get PresentationState(): PresentationState { return this._presentationState; }

    /* ---------------------------------------- */

    /* NOTE: this set function should ONLY be used by the PresentationController. */
    public set timeline(tl: gsap.core.Timeline) { this._timeline = tl; }

    /**
     * The Core TimeLine that is being handled by the PresentationController that owns the Presentaion. 
     * 
     * Apply any other TimeLines, tweens and/or function calls to this timeline to fill up the presentation flow.
     * When this Timeline Ends. It will try to Trigger the Stop, but if the function "IsActive" and/or "isFinished" returns true.
     * This Timeline will Reset itself and ReTrigger Function GetPresentation or EndPresentation.
     * 
     * @returns {gsap.core.Timeline}.
    */
    public get timeline(): gsap.core.Timeline { return this._timeline; }

    /**
     * Use this if you want to use a secondary checker other than function IsActive.
     * This function will only be checked if applied and the presentation has been played ones.
     * 
     * @returns {boolean} True = Retrigger the fetch. False = Trigger done.
    */
    public isFinished(): boolean { return true; }

    /**
     * Use this if you want to Initialize something at the same time as the Presentation is being defined.
     * 
     * @returns {void} 
    */
    public initialization?(): void;


    public abstract createPresentation(enableSkip: Function): void;

    /**
     * Use this if you want to Define your own continous activation.
     * 
     * NOTE: If this is not being defined. Use "this.PresentationState.Set(this.PresentationState.INACTIVE);"
     * to disable it so it won't get stuck in a infinity loop.
     * 
     * This function needs to be true if the presentation should be played.
     * 
     * @returns {boolean} True = Retrigger the Get/EndPresentation. False = Trigger StopPresentation.
    */
    public isActive(): boolean { return this.PresentationState.isActive; }

    /**
    * Use this if you want to add some EventListeners on init.
    * 
    * @returns {void}
    */
    public addEventListeners?(): void;

    /**
     * Does NOT include TimeLine(!).
     * 
     * Use this function to PreDetermine if the current presentation should get any new "ParsedGameData" and therefor be activated.
     * 
     * @param data "ParsedGameData"
     * @returns {boolean} True: Triggers Function setParsedGamedData(data:any) and sets PresentationState to ACTIVATED.
    */
    public checkForActivation(data: any): boolean { return true; }

    /**
     * Does NOT include TimeLine(!).
     * 
     * Function that will be called ones before triggering getPresentation for the first time.
     * 
     * @returns {void}
    */
    public preStart?(): void;

    /**
     * Includes TimeLine(!).
     * 
     * Function that catches the Trigger events other than Bet.
     * 
     * This function will only be triggered if defined and if the current Presentation isActive.
     * 
     * @returns {void}
    */
    public skip(): void { this.timeline && this.timeline.isActive() ? this.timeline.totalProgress(1) : undefined; }

    /**
     * Does NOT include TimeLine(!).
     * 
     * This function will only be triggered if function: checkForActivation(data: any) returns true.
     * 
     * @param data "parsedGameData"
     * @returns {void}
    */
    public setGamedData?(data: any): void;

    /**
     * Does NOT include TimeLine(!).
     * 
     * This function will always be triggered during GameInit, if applied.
     * 
     * @param data "InitServerData"
     * @returns {void}
    */
    public setInitData?(data: any): void;

    /**
     * Does NOT include TimeLine(!).
     * 
     * The last Functional call before going to the next presentation.
     * 
     * @returns {void}
    */
    public done?(): void;
}